import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import dateFormat from "dateformat";
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { apiError } from '../../../helpers/ErrorHelper';
import { useAuth } from '../../../context/AuthContext';
import { createInvoice, saveInvoicesToStorage } from '../../../models/InvoiceModel';
import { getData } from '../../../models/ClientsModel';
import { getData as getClientBranches } from '../../../models/ClientBranchesModel';
import Select from 'react-select';
import { useTheme } from '../../../context/ThemeContext';
import { useNotificationCount } from '../../../context/NotificationCountContext';

const matchClientId = (a, b) => String(a ?? '') === String(b ?? '');

const CreateInvoiceModal = ({
  showModal,
  setShowModal,
  fetchInvoices,
  limit,
  page,
  _client_id,
}) => {
  const { setNotificationCount } = useNotificationCount();
  const { selectBoxTheme } = useTheme();
  const navigate = useNavigate();
  const { state } = useAuth();
  const user_data = state.user_data;
  const loader = document.getElementById('loading');

  const [loading, setLoading] = useState(false);
  const [invoice_client_id, setInvoiceClientID] = useState('');
  const [invoice_branch_id, setInvoiceBranchID] = useState('');
  const [branchOptions, setBranchOptions] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [invoice_bill_date, setInvoiceBillDate] = useState('');
  const [invoice_due_date, setInvoiceDueDate] = useState('');
  const [invoice_month, setInvoiceMonth] = useState('');
  const [invoice_po_number, setInvoicePONumber] = useState('');
  const [errors, setErrors] = useState({
    invoice_client_id: '',
    invoice_branch_id: '',
    invoice_bill_date: '',
    invoice_due_date: '',
    invoice_month: '',
    invoice_po_number: '',
  });
  const [clients, setClients] = useState([]);

  const closeModal = () => {
    setInvoiceClientID('');
    setInvoiceBranchID('');
    setBranchOptions([]);
    setInvoiceBillDate('');
    setInvoiceDueDate('');
    setInvoiceMonth('');
    setInvoicePONumber('');
    setErrors({});
    setShowModal(false);
  };

  const loadBranchesForClient = async (clientId, { autoCreate = true } = {}) => {
    if (!clientId) {
      setBranchOptions([]);
      setInvoiceBranchID('');
      return;
    }
    setBranchesLoading(true);
    try {
      const response = await getClientBranches({
        user_token: user_data.user_token,
        client_branch_client_id: clientId,
        limit: -1,
        ...(autoCreate ? { auto_create: '1' } : {}),
      });
      const rows = response?.client_branches || [];
      if (autoCreate && response?.branch_auto_created) {
        toast.info('Default billing branch was created from client details.');
      }
      if (autoCreate && Number(response?.invoices_linked) > 0) {
        toast.info(`${response.invoices_linked} invoice(s) linked to the default branch.`);
      }
      const options = rows.map((b) => ({
        value: b._client_branch_id,
        label: [
          b.client_branch_name,
          b.client_branch_gstin ? `(${b.client_branch_gstin})` : null,
          Number(b.client_branch_is_default) === 1 ? '— Default' : null,
        ].filter(Boolean).join(' '),
        isDefault: Number(b.client_branch_is_default) === 1,
      }));
      setBranchOptions(options);
      const defaultOpt =
        options.find((o) => o.isDefault) || (options.length === 1 ? options[0] : null);
      setInvoiceBranchID(defaultOpt || '');
    } catch (error) {
      setBranchOptions([]);
      setInvoiceBranchID('');
    } finally {
      setBranchesLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const result = await getData({ user_token: user_data.user_token, page: 1, limit: 500 });
      const clientList = result?.clients || [];
      const clientOptions = clientList.map((client) => ({
        label: `${client.client_name} (${client.client_gstin || 'No GSTIN'})`,
        value: client._client_id,
      }));
      setClients(clientOptions);

      if (_client_id) {
        const selectedClient = clientList.find((client) =>
          matchClientId(client._client_id, _client_id)
        );
        if (selectedClient) {
          setInvoiceClientID({
            label: selectedClient.client_name,
            value: selectedClient._client_id,
          });
          await loadBranchesForClient(selectedClient._client_id);
        }
      }

      setNotificationCount(localStorage.getItem('unread_notification_count'));
    } catch (error) {
      // ignore — modal still usable
    }
  };

  const clientChange = async (clientObj) => {
    setInvoiceClientID(clientObj || '');
    const clientId = clientObj?.value || '';
    await loadBranchesForClient(clientId);
  };

  useEffect(() => {
    if (showModal) {
      fetchClients();
    }
  }, [showModal, _client_id]);

  const addInvoice = async () => {
    loader.style.display = 'block';
    setLoading(true);

    try {
      setErrors({});

      const formData = {
        invoice_client_id: invoice_client_id?.value ? invoice_client_id.value : '',
        invoice_branch_id: invoice_branch_id?.value ? invoice_branch_id.value : '',
        invoice_bill_date: invoice_bill_date ? dateFormat(invoice_bill_date, 'dd-mm-yyyy') : '',
        invoice_due_date: invoice_due_date ? dateFormat(invoice_due_date, 'dd-mm-yyyy') : '',
        invoice_month: invoice_month ? dateFormat(invoice_month, 'mmm-yyyy') : '',
        invoice_po_number: invoice_po_number ? invoice_po_number : '',
      };

      const response = await createInvoice(user_data.user_token, formData);

      if (response.status) {
        toast.success(response.message);
        closeModal();
        saveInvoicesToStorage(response.data.invoices, response.data.pagination);
        fetchInvoices(limit, page);
        setNotificationCount(localStorage.getItem('unread_notification_count'));
      } else {
        setErrors(response.error || {});
        toast.error(response.message);
      }
    } catch (error) {
      apiError(error);
      if (error.response?.data?.invalid_token === 1) {
        navigate('/re-login');
      }
    }

    loader.style.display = 'none';
    setLoading(false);
  };

  return (
    <Modal show={showModal} onHide={closeModal} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Create Invoice</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row gy-2">
          <div className="col-xl-12">
            <label className="form-label">
              Client <span className="text-danger">*</span>
            </label>
            <Select
              className="form-control custom-select"
              value={invoice_client_id}
              onChange={clientChange}
              options={clients}
              placeholder="- Client -"
              isSearchable
              noOptionsMessage={() => 'No client found'}
              styles={selectBoxTheme}
              theme={(theme) => ({
                ...theme,
                borderRadius: 0,
              })}
            />
            {errors.invoice_client_id && (
              <p style={{ color: 'red' }}>{errors.invoice_client_id}</p>
            )}
          </div>

          {invoice_client_id?.value ? (
            <div className="col-xl-12">
              <label className="form-label">Billing Branch (GSTIN / Address)</label>
              <Select
                className="form-control custom-select"
                value={invoice_branch_id}
                onChange={setInvoiceBranchID}
                options={branchOptions}
                placeholder={
                  branchesLoading
                    ? 'Loading branches...'
                    : branchOptions.length
                      ? '- Billing branch -'
                      : 'Default branch will be created on save'
                }
                isSearchable
                isClearable
                isLoading={branchesLoading}
                isDisabled={branchesLoading}
                noOptionsMessage={() =>
                  branchesLoading
                    ? 'Loading...'
                    : 'Default branch will be created automatically when you save'
                }
                styles={selectBoxTheme}
                theme={(theme) => ({
                  ...theme,
                  borderRadius: 0,
                })}
              />
              <small className="text-muted">
                Invoice PDF uses this branch for GSTIN and billing address. If none
                exists, the server creates a default branch from the client record.
              </small>
              {errors.invoice_branch_id && (
                <p style={{ color: 'red' }}>{errors.invoice_branch_id}</p>
              )}
            </div>
          ) : null}

          <div className="col-xl-6">
            <label className="form-label">
              Bill Date <span className="text-danger">*</span>
            </label>
            <br />
            <DatePicker
              className="form-control"
              placeholderText="DD-MM-YYYY"
              minDate={new Date()}
              maxDate={invoice_due_date}
              selected={invoice_bill_date}
              onSelect={setInvoiceBillDate}
              onChange={setInvoiceBillDate}
              dateFormat="dd-MM-yyyy"
            />
            {errors.invoice_bill_date && (
              <p style={{ color: 'red' }}>{errors.invoice_bill_date}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              Due Date <span className="text-danger">*</span>
            </label>
            <br />
            <DatePicker
              className="form-control"
              placeholderText="DD-MM-YYYY"
              minDate={invoice_bill_date ? invoice_bill_date : new Date()}
              disabled={!invoice_bill_date}
              selected={invoice_due_date}
              onSelect={setInvoiceDueDate}
              onChange={setInvoiceDueDate}
              dateFormat="dd-MM-yyyy"
            />
            {errors.invoice_due_date && (
              <p style={{ color: 'red' }}>{errors.invoice_due_date}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              Invoice Month <span className="text-danger">*</span>
            </label>
            <br />
            <DatePicker
              className="form-control"
              placeholderText="MMM-YYYY"
              maxDate={invoice_due_date}
              selected={invoice_month}
              showMonthYearPicker
              onSelect={setInvoiceMonth}
              onChange={setInvoiceMonth}
              dateFormat="MMM-yyyy"
            />
            {errors.invoice_month && (
              <p style={{ color: 'red' }}>{errors.invoice_month}</p>
            )}
          </div>

          <div className="col-xl-6">
            <label className="form-label">
              PO Number <span className="text-danger">*</span>
            </label>
            <br />
            <input
              type="text"
              className="form-control"
              placeholder="PO Number"
              value={invoice_po_number}
              onChange={(e) => setInvoicePONumber(e.target.value)}
            />
            {errors.invoice_po_number && (
              <p style={{ color: 'red' }}>{errors.invoice_po_number}</p>
            )}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-light" onClick={closeModal}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={addInvoice}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm align-middle"></span>
              Loading...
            </>
          ) : (
            'Submit'
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateInvoiceModal;
